import { FormikImageUpload } from 'components/v2/images-upload/FormikImageUpload';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useRef } from 'react';
import styled from 'styled-components';
import { Label } from 'styles/Styles.styled';
import { CollocationCenterModel, Option } from 'types/types';
import * as Yup from 'yup';

import { StyledReactSelect } from '../../../../styles/Styles.styled';
import PrimeInputTextField from '../../form/PrimeInputTextField';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Collocation center name is required'),
  countries: Yup.array().min(1, 'Required'),
  logo: Yup.mixed()
    .required('Logo is required')
    .test('is-valid-logo', 'Logo is required', (value) => {
      if (!value) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (value instanceof File) return value.size > 0;
      return false;
    }),
});

type TProps = {
  show: boolean;
  onHide: () => void;
  onSubmit: (item: CollocationCenterModel) => void;
  item?: CollocationCenterModel;
  countriesLookup: Option[];
};

const EditCollocationCenterDialog = ({ show, onHide, onSubmit, item, countriesLookup }: TProps) => {
  const formRef = useRef<any>();

  const initialValues: any = item
    ? {
        name: item.name,
        description: item.description,
        countryCodes: item.countryCodes,
        countries: item.countryCodes
          ?.map((code) => countriesLookup.find((item) => item.value === code))
          .filter((item): item is { value: string; label: string } => !!item),
        logotypeUrl: item.logotypeUrl,
        logo: item.logotypeUrl,
      }
    : {
        name: '',
        description: '',
        countries: [],
        localHomepage: '',
        logotypeUrl: '',
        logo: null,
      };

  const handleUpdate = async () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleCancel = async () => {
    onHide();
  };

  const dialogFooter = (
    <div className="custom-dialog">
      <Button label="Cancel" onClick={handleCancel} className="p-button-text" />
      <Button label="Save" onClick={handleUpdate} autoFocus />
    </div>
  );

  return (
    <Dialog
      header="Edit collocation center"
      visible={show}
      style={{ minHeight: '80vh', maxWidth: '33rem', width: '33rem', minWidth: '20rem' }}
      onHide={onHide}
      footer={dialogFooter}
    >
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form>
            <Column>
              <FieldGroup>
                <Label required>Collocation center name</Label>
                <PrimeInputTextField name="name" className={'w-full'} type="text" placeholder="Name" />
              </FieldGroup>

              <FieldGroup>
                <Label required>Description</Label>
                <PrimeInputTextField name="description" className={'w-full'} type="text" placeholder="Description" />
              </FieldGroup>

              <FieldGroup>
                <Label required>Countries</Label>
                <StyledReactSelect
                  name={'countries'}
                  className={'w-full'}
                  options={countriesLookup}
                  isMulti
                  isClearable
                  placeholder="Pick countries in the collocation center"
                />
              </FieldGroup>
              <FieldGroup>
                <div>
                  <SmartLabel
                    label="Collocation center logo"
                    required={!(values as any)?.logo}
                    tooltip="Logo will be used in the EPDs issued by this CLC. It will be displayed on the cover page of the EPD document."
                  />
                  <FormikImageUpload name="logo" />
                </div>
              </FieldGroup>
            </Column>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const Column = styled.div`
  padding: 0 2rem;
  display: grid;
  grid-column: auto;
  row-gap: 1rem;
  box-sizing: border-box;
  margin: 1rem 0;
`;

const FieldGroup = styled.div`
  width: 100%;
  margin: 0;
`;

export default EditCollocationCenterDialog;
